"use client";

import { unstable_isUnrecognizedActionError } from "next/navigation";
import { useEffect, useMemo } from "react";
import { claimUpdateScreen, markUpdateBlocked } from "./app-update";

/**
 * "Is this error boundary looking at a redeploy rather than a bug?"
 *
 * Next 16 answers that precisely, which is the whole reason this can be a
 * statement rather than a guess. When a stale tab posts a server action the new
 * server no longer has, the response is a 404 carrying
 * `x-nextjs-action-not-found: 1`; the client router turns that specific header
 * into an `UnrecognizedActionError`, and `unstable_isUnrecognizedActionError`
 * identifies it. So the boundary is not inferring a deploy from a generic
 * failure — it is reading the server saying "that action does not exist here".
 *
 * ⚠️ **Do not reach for `error.digest` or the message text instead.** In
 * production Next redacts a server error's message to a constant and a digest is
 * an opaque hash, so a message-sniffing version of this would match nothing —
 * and a `digest`-based one would match everything. This predicate works because
 * the failure is thrown in the BROWSER, by the router, as a real instance.
 *
 * ⚠️ **It cannot see the no-JS path, and does not need to.** A `<form action>`
 * submitted without JavaScript posts `$ACTION_ID_*` as multipart, and Next
 * throws that one server-side (E975) rather than answering the header — so it
 * arrives here as an ordinary redacted server error. That case self-heals: the
 * error page came from the NEW server with the new bundle, so the retry works.
 * The JS path is the one that cannot heal itself, and that is the one this
 * covers.
 *
 * While it is true the boundary owns the "reload" message, so the corner banner
 * stays quiet — see `claimUpdateScreen`.
 */
export function useStaleDeploymentError(error: unknown): boolean {
	// Keyed on `error`, not on first mount: a boundary that is reset and then hit
	// by a DIFFERENT failure keeps the same component instance, so a once-only
	// initialiser would report the first error's verdict for the second one.
	const isStaleDeploy = useMemo(() => {
		try {
			return unstable_isUnrecognizedActionError(error);
		} catch {
			// An `unstable_` export that changed shape must not turn a handled error
			// screen into a blank one. Same contract as `useReportBoundaryError`.
			return false;
		}
	}, [error]);

	useEffect(() => {
		if (!isStaleDeploy) return;
		markUpdateBlocked();
		return claimUpdateScreen();
	}, [isStaleDeploy]);

	return isStaleDeploy;
}
